import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const HIGHLIGHT_DURATION_MS = 1000;

type JustAddedFacts = {
    justAddedKeys: Set<string>;
    markJustAdded: (keys: string[]) => void;
};

/* Default (no provider, e.g. the Leads tab): nothing is ever marked, so nothing highlights. */
const JustAddedFactsContext = createContext<JustAddedFacts>({
    justAddedKeys: new Set(),
    markJustAdded: () => {},
});

/* Map a fact suggestion key to the sidebar row key it lands in. */
export function rowKeyForFact(factKey: string): string {
    if (factKey === 'monthly_income' || factKey === 'annual_income') {
        return 'income';
    }
    if (factKey === 'phone_number') {
        return 'phone';
    }
    return factKey;
}

/*
 * Tracks the sidebar rows just added by accepting a suggestion, so they briefly
 * highlight. Driven by the accept action (not by render diffing), and scoped to this
 * provider — outside it `justAddedKeys` is always empty, so other pages never flash.
 */
export function JustAddedFactsProvider({ children }: { children: React.ReactNode }) {
    const [justAddedKeys, setJustAddedKeys] = useState<Set<string>>(new Set());

    const markJustAdded = useCallback((keys: string[]) => {
        if (keys.length === 0) {
            return;
        }
        setJustAddedKeys((existing) => new Set([...existing, ...keys]));
        setTimeout(() => {
            setJustAddedKeys((existing) => {
                const next = new Set(existing);
                keys.forEach((key) => next.delete(key));
                return next;
            });
        }, HIGHLIGHT_DURATION_MS);
    }, []);

    const value = useMemo(() => ({ justAddedKeys, markJustAdded }), [justAddedKeys, markJustAdded]);
    return <JustAddedFactsContext.Provider value={value}>{children}</JustAddedFactsContext.Provider>;
}

/* Row keys currently highlighted (used by the sidebar). */
export function useJustAddedKeys(): Set<string> {
    return useContext(JustAddedFactsContext).justAddedKeys;
}

/* Mark sidebar rows as just-added (called from the accept handler). */
export function useMarkJustAdded(): (keys: string[]) => void {
    return useContext(JustAddedFactsContext).markJustAdded;
}
